import React, { useEffect, useRef, useState } from 'react';
import useTranslate from '../../../../../common/hooks/useTranslate';
import { pagedDataGenerator } from '../../../../ui/table/pagedDataResults/pagedDataGenerator';
import { useDispatch, useSelector } from 'react-redux';
import useBoardContentWidget from '../../../hooks/useBoardContentWidget';
import { POPUP } from '../../../../../redux/types';
import { filterDocuments } from '../../../../services/documentsService';
import { RootState } from '../../../../../redux/store';

import { WidgetProps } from '../../../types/IWidgetProps';
import { IGridSetup } from '../grid/types/IGridSetup';

import { SortDescriptor } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';

import ContentLoading from '../../../../ui/loaders/contentLoading/contentLoading';
import WidgetWrapper from '../widgetWrapper';
import ChipsCell from '../../../../documents/components/cells/chipsCell/chipsCell';
import DocumentNameCell from '../../../../documents/components/cells/documentNameCell/documentNameCell';
import DateCell from '../../../../documents/components/cells/dateCell/dateCell';

import './documentWidget.scss';

const DocumentWidget: React.FC<WidgetProps> = ({
                                                 widget,
                                                 selectedObject,
                                                 selectedPeriod,
                                                 customisation,
                                                 moveWidget,
                                                 removeWidget,
                                                 content,
                                                 homeContent,
                                                 widgetView,
                                                 handleUnpinWidget,
                                                 handlePinWidget,
                                                 handleUnpinTrigger,
                                                 unpinId,
                                               }) => {
  const pageSize = 75;
  const initialSort: Array<SortDescriptor> = [{ field: 'date', dir: 'desc' }];

  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsSetup, setDocumentsSetup] = useState<IGridSetup>();
  const [page, setPage] = useState<any>({ skip: 0, take: pageSize, includeCount: true });
  const [totalResultCount, setTotalResultCount] = useState(0);
  const [fetching, setFetching] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const { widgetSetup } = useBoardContentWidget();

  const investor = useSelector((state: RootState) => state.investor.investor);

  const { t } = useTranslate();
  const fetchingData = useRef(false);
  const dispatch = useDispatch();

  const ignoreDates = JSON.parse(widget.setup).ignoreDates;

  //@ts-ignore
  let fromDateValue = ignoreDates ? null : widget.setup.singlePeriod ? selectedPeriod.startDate.split('T')[0] : null;
  //@ts-ignore
  let toDateValue = ignoreDates ? null : selectedPeriod.endDate.split('T')[0];

  const fetchData = (page: any, funds, investors, classifications, fromDate, toDate) => {
    if (fetchingData.current) return Promise.resolve();
    fetchingData.current = true;
    setFetching(true);

    const skipResults = Math.max(page.skip - page.take, 0);

    return filterDocuments(null, funds, investors, classifications, null, fromDate, toDate, skipResults, page.take * 3, initialSort[0].field, initialSort[0].dir, false, true, null, null, page.includeCount)
      .then((results) => {
        setDocuments(pagedDataGenerator(results, page, documents, skipResults));
        if (page.includeCount) setTotalResultCount(results.totalCount);
        setFetching(false);
        setLoading(false);
      })
      .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }))
      .finally(() => {
        fetchingData.current = false;
      });
  };

  const DocNameCell = (props) => <DocumentNameCell {...props} origin='docWidget' alterField='name' />;

  useEffect(() => {
    const selectedObjectPayload = { ...selectedObject, id: selectedObject.objectId };
    const investorPayload = { name: investor.name, objectId: investor.id };

    if (widgetSetup(widget))
      setDocumentsSetup(widgetSetup(widget));

    if (selectedPeriod && selectedObject) {
      setDocuments([]);
      setTotalResultCount(0);

      fetchData({
        skip: 0,
        take: pageSize,
        includeCount: true,
      }, [selectedObjectPayload], [investorPayload], widget.setup.documentClassifications, fromDateValue, toDateValue);
    }
  }, [selectedObject, widget, investor, selectedPeriod]);

  useEffect(() => {
    const selectedObjectPayload = { ...selectedObject, id: selectedObject.objectId };
    const investorPayload = { name: investor.name, objectId: investor.id };

    const handlePageChange = setTimeout(() => {
      for (let i = page.skip; i < page.skip + pageSize && i < documents.length; i++) {
        if (documents[i].id === undefined) {
          fetchData(page, [selectedObjectPayload], [investorPayload], widget.setup.documentClassifications, fromDateValue, toDateValue)
            .finally(() => {
              setLoading(false);
            });
          return;
        }
      }
    }, 200);

    return () => clearTimeout(handlePageChange);
  }, [page]);

  return window.innerWidth < 960 && documentsSetup?.hideOnMobile ? null : (
    <WidgetWrapper
      widget={widget}
      customisation={customisation}
      selectedObject={selectedObject}
      moveWidget={moveWidget}
      homeContent={homeContent}
      widgetView={widgetView}
      content={content}
      removeWidget={removeWidget}
      handleUnpin={handleUnpinTrigger}
      handleUnpinFromBoardCallback={handleUnpinWidget}
      handlePinCallback={handlePinWidget}
      unpinId={unpinId}>
      {loading ? (
        <ContentLoading />
      ) : (
        <div className='doc-widget-wrapper'>
          <Grid
            data={documents.slice(page.skip, page.skip + pageSize)}
            dataItemKey={'id'}
            resizable={true}
            rowHeight={50}
            pageSize={pageSize}
            total={totalResultCount}
            skip={page.skip}
            scrollable={'virtual'}
            onPageChange={(event) => setPage({ ...event.page, includeCount: false })}
            className='info-table-style-wrapper nowrap-table'>
            <GridNoRecords>{fetching ? t('LOADING_DATA') : t('NO_RECORDS_AVAILABLE')}</GridNoRecords>
            <GridColumn
              field='label'
              title={t('NAME')}
              width={400}
              cell={DocNameCell}
            />
            <GridColumn field='date' title={t('DOCUMENTS_TABLE.COLUMN_EFFECTIVE_DATE')} width={150} format={'{0:d}'}
                        cell={(props) => <DateCell {...props} format='MM/dd/yyyy' />} />
            <GridColumn field='dueDate' title={t('DOCUMENTS_TABLE.DUE_DATE')} width={150} format={'{0:d}'}
                        cell={(props) => <DateCell {...props} format='MM/dd/yyyy' />} />
            <GridColumn field='classifications' title={t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS')}
                        cell={(props) => <ChipsCell {...props} useTooltip={true} />} width={300} />
            <GridColumn field='fundName' title={t('DOCUMENTS_TABLE.COLUMN_FUND')} width={400} />
            <GridColumn field='fundInvestorName' title={t('DOCUMENTS_TABLE.COLUMN_INVESTOR')} width={400} />
            <GridColumn field='publishDate' title={t('DOCUMENTS_TABLE.COLUMN_POSTED')} width={150} format={'{0:d}'}
                        cell={(props) => <DateCell {...props} format='MM/dd/yyyy' />} />
          </Grid>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default DocumentWidget;
